/**
 * Quem está escrevendo em cada conversa (docs/domain/equipes.md §7.2).
 *
 * Vale por segundos e não vai ao servidor como estado: o evento chega pelo stream e
 * vence sozinho. É a defesa contra duas pessoas respondendo a mesma paciente.
 */
import { useSyncExternalStore } from 'react';

const VALE_MS = 6_000;

const escrevendo = new Map<string, Map<string, number>>();
const ouvintes = new Set<() => void>();
let versao = 0;

function avisarOuvintes(): void {
  versao += 1;
  for (const o of ouvintes) o();
}

export function marcarEscrevendo(conversationId: string, userId: string): void {
  const naConversa = escrevendo.get(conversationId) ?? new Map<string, number>();
  naConversa.set(userId, Date.now() + VALE_MS);
  escrevendo.set(conversationId, naConversa);
  avisarOuvintes();
  setTimeout(avisarOuvintes, VALE_MS + 50);
}

function quemEscreve(conversationId: string, eu: string | null): readonly string[] {
  const naConversa = escrevendo.get(conversationId);
  if (naConversa === undefined) return [];
  const agora = Date.now();
  const vivos: string[] = [];
  for (const [userId, ate] of naConversa) {
    if (ate < agora) naConversa.delete(userId);
    else if (userId !== eu) vivos.push(userId);
  }
  return vivos;
}

const cache = new Map<string, { versao: number; valor: readonly string[] }>();

/** Os ids de quem está escrevendo agora nesta conversa, sem contar quem pergunta. */
export function useEscrevendo(conversationId: string | null, eu: string | null): readonly string[] {
  return useSyncExternalStore(
    (o) => {
      ouvintes.add(o);
      return () => ouvintes.delete(o);
    },
    () => {
      if (conversationId === null) return VAZIO;
      const chave = `${conversationId}|${eu ?? ''}`;
      const c = cache.get(chave);
      if (c !== undefined && c.versao === versao) return c.valor;
      const valor = quemEscreve(conversationId, eu);
      const anterior = c?.valor;
      const igual =
        anterior !== undefined &&
        anterior.length === valor.length &&
        anterior.every((v, i) => v === valor[i]);
      const final = igual ? anterior : valor;
      cache.set(chave, { versao, valor: final });
      return final;
    },
  );
}

const VAZIO: readonly string[] = [];
